import type {
    AccessDeniedPresentation,
    AccessRequestContext,
    CallerHint,
    DenialClassification,
    DenialSource,
    ResourceDenialSubtype,
} from "./accessDeniedTypes";
import { getDenialSourceForCode, isLifecycleDenyCode } from "./denialCodes";
import { isDetailEditPathname, isListPathname } from "./routeAccessLabels";

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const HINT_TO_CLASSIFICATION: Record<
    CallerHint,
    { source: DenialSource; resourceSubtype?: ResourceDenialSubtype }
> = {
    editDetailLoad: { source: "resource", resourceSubtype: "detail" },
    siteDetailLoad: { source: "resource", resourceSubtype: "detail" },
    pagedListFilter: { source: "resource", resourceSubtype: "embedded" },
    summaryWidget: { source: "resource", resourceSubtype: "embedded" },
    sitePicker: { source: "resource", resourceSubtype: "embedded" },
    adminListPost: { source: "resource", resourceSubtype: "embedded" },
    formSave: { source: "action" },
    lifecycleMutation: { source: "action" },
    modalSave: { source: "action" },
    routeGuard: { source: "route" },
};

export type ClassifyDenialInput = {
    code?: string;
    message?: string;
    httpStatus?: number;
    context?: AccessRequestContext;
};

function normalizeMethod(method?: string): string {
    return (method ?? "GET").toUpperCase();
}

/** Infer HTTP method from request URL when not supplied. */
export function inferMethodFromRequestUrl(requestUrl: string | undefined): string {
    if (!requestUrl) return "GET";
    const lower = requestUrl.toLowerCase();
    if (
        lower.includes("/all") ||
        lower.includes("/summary") ||
        lower.includes("/disable") ||
        lower.includes("/enable") ||
        lower.includes("/restore") ||
        lower.includes("/softdelete") ||
        lower.includes("/add") ||
        lower.includes("/create") ||
        lower.includes("/update") ||
        lower.includes("/save")
    ) {
        return "POST";
    }
    return "GET";
}

function isEmbeddedListRequestUrl(requestUrl: string | undefined): boolean {
    if (!requestUrl) return false;
    const lower = requestUrl.toLowerCase();
    return lower.includes("/all") || lower.includes("/summary");
}

function isMutationMethod(method: string): boolean {
    return MUTATION_METHODS.has(method);
}

function deliveryFor(source: DenialSource, resourceSubtype?: ResourceDenialSubtype) {
    if (source === "route" || (source === "resource" && resourceSubtype === "detail")) {
        return { redirectToAccessDenied: true, delivery: "page" as const };
    }
    if (source === "resource" && resourceSubtype === "embedded") {
        return { redirectToAccessDenied: false, delivery: "inlineAlert" as const };
    }
    if (source === "action") {
        return { redirectToAccessDenied: false, delivery: "toast" as const };
    }
    return { redirectToAccessDenied: false, delivery: "banner" as const };
}

function classifyFromCallerHint(hint: CallerHint): DenialClassification | undefined {
    const mapped = HINT_TO_CLASSIFICATION[hint];
    const { redirectToAccessDenied, delivery } = deliveryFor(mapped.source, mapped.resourceSubtype);
    return {
        source: mapped.source,
        resourceSubtype: mapped.resourceSubtype,
        redirectToAccessDenied,
        delivery,
    };
}

function inferResourceSubtype(
    method: string,
    pathname: string,
    requestUrl: string | undefined
): ResourceDenialSubtype {
    if (isMutationMethod(method) || isEmbeddedListRequestUrl(requestUrl)) {
        return "embedded";
    }
    if (isDetailEditPathname(pathname)) {
        return "detail";
    }
    if (isListPathname(pathname)) {
        return "embedded";
    }
    return "detail";
}

/**
 * Layered denial classification. callerHint is optional and takes priority when supplied.
 * Order: callerHint → lifecycle/action/route codes → path/URL → HTTP method → SiteNotAccessible → fallback.
 * Safe default: mutations never redirect; unknown mutation → action + toast.
 */
export function classifyDenial(input: ClassifyDenialInput): DenialClassification {
    const context = input.context ?? {};
    const pathname = context.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
    const requestUrl = context.requestUrl;
    const method = normalizeMethod(context.method ?? inferMethodFromRequestUrl(requestUrl));
    const code = input.code;

    // 1. Explicit callerHint (highest priority)
    if (context.callerHint) {
        if (isLifecycleDenyCode(code)) {
            return { source: "lifecycle", redirectToAccessDenied: false, delivery: "banner" };
        }
        const fromHint = classifyFromCallerHint(context.callerHint);
        if (fromHint) return fromHint;
    }

    // 2. Lifecycle error codes
    if (isLifecycleDenyCode(code)) {
        return { source: "lifecycle", redirectToAccessDenied: false, delivery: "banner" };
    }

    // 3. Strong action/route error codes (override path heuristics)
    const codeSource = getDenialSourceForCode(code);
    if (codeSource === "action") {
        return { source: "action", redirectToAccessDenied: false, delivery: "toast" };
    }
    if (codeSource === "route") {
        return { source: "route", redirectToAccessDenied: true, delivery: "page" };
    }

    // 4. Route/path heuristics — list/search POST and list pages → embedded
    if (isEmbeddedListRequestUrl(requestUrl) || isListPathname(pathname)) {
        return {
            source: "resource",
            resourceSubtype: "embedded",
            redirectToAccessDenied: false,
            delivery: "inlineAlert",
        };
    }

    // 5. HTTP method heuristics — mutations default to action (stay on page, toast)
    if (isMutationMethod(method)) {
        return { source: "action", redirectToAccessDenied: false, delivery: "toast" };
    }

    // 6. GET detail/edit routes
    if (isDetailEditPathname(pathname)) {
        return {
            source: "resource",
            resourceSubtype: "detail",
            redirectToAccessDenied: true,
            delivery: "page",
        };
    }

    // 7. SiteNotAccessible — resource with subtype inferred from context
    if (codeSource === "resource") {
        const subtype = inferResourceSubtype(method, pathname, requestUrl);
        const { redirectToAccessDenied, delivery } = deliveryFor("resource", subtype);
        return { source: "resource", resourceSubtype: subtype, redirectToAccessDenied, delivery };
    }

    // 8. Safe fallback
    if (isMutationMethod(method)) {
        return { source: "action", redirectToAccessDenied: false, delivery: "toast" };
    }
    if (isEmbeddedListRequestUrl(requestUrl) || isListPathname(pathname)) {
        return {
            source: "resource",
            resourceSubtype: "embedded",
            redirectToAccessDenied: false,
            delivery: "inlineAlert",
        };
    }
    return {
        source: "resource",
        resourceSubtype: "detail",
        redirectToAccessDenied: true,
        delivery: "page",
    };
}

export function resolveAccessDeniedPresentation(args: {
    source: DenialSource;
    resourceSubtype?: ResourceDenialSubtype;
    code?: string;
    message?: string;
    resourceLabel?: string;
    requiredRoles?: string[];
}): AccessDeniedPresentation {
    const serverMessage = args.message?.trim();
    const resource = args.resourceLabel?.trim();

    if (args.source === "route") {
        const roles =
            args.requiredRoles && args.requiredRoles.length > 0
                ? args.requiredRoles.join(" or ")
                : "Administrator";
        return {
            title: "Access not available",
            explanation:
                serverMessage ??
                (resource
                    ? `You don't have permission to open ${resource}.`
                    : "You don't have permission to open this area."),
            nextStep: `This area requires ${roles} access. Return to the dashboard or contact an Administrator.`,
            severity: "warning",
        };
    }

    if (args.source === "resource" && args.resourceSubtype === "detail") {
        return {
            title: resource ? `${resource} not available` : "Record not available",
            explanation:
                serverMessage ??
                "You don't have access to this record, or it isn't available in your organisation.",
            nextStep: "Open the record from the list, or check that you have access to the correct site.",
            severity: "warning",
        };
    }

    if (args.source === "resource" && args.resourceSubtype === "embedded") {
        return {
            title: "Filter not available",
            explanation: serverMessage ?? "The selected site or filter isn't available for your account.",
            nextStep: "Choose another site or clear the filter and try again.",
            severity: "warning",
        };
    }

    if (args.source === "action") {
        return {
            title: "Action not allowed",
            explanation: serverMessage ?? "You don't have permission to complete that action.",
            nextStep: "Contact an Administrator or Yard/Office Staff if you need this changed.",
            severity: "danger",
        };
    }

    return {
        title: "Read-only",
        explanation: serverMessage ?? "This record or site is in a state that prevents changes.",
        nextStep: "Review the banner on the page for what you can do next.",
        severity: "warning",
    };
}

export function getActionDenialMessage(error: unknown, fallback: string): string {
    if (typeof error === "string") {
        const t = error.trim();
        return t.length > 0 ? t : fallback;
    }
    if (error && typeof error === "object" && "message" in error) {
        const msg = (error as { message: unknown }).message;
        if (typeof msg === "string" && msg.trim().length > 0) return msg.trim();
    }
    const presentation = resolveAccessDeniedPresentation({ source: "action", message: undefined });
    return presentation.explanation !== "You don't have permission to complete that action."
        ? presentation.explanation
        : fallback;
}
